import { Entity } from '@/lib/realtime/RealtimeEntity';
import { Where } from '@/lib/realtime/weak/realtimeFiltering';
import {
  Absence,
  AbsenceCategory,
  AccessRole,
  BillingCouponRedemption,
  BillingDetails,
  BillingPackage,
  BillingPaymentMethod,
  BillingProductPrice,
  BillingWallet,
  BillingWalletType,
  BudgetSummary,
  ClockInPortal,
  Company,
  CompanyNotificationChannel,
  CompanyOnboarding,
  CompanySetting,
  DaySummary,
  DigiticketsConnection,
  DigiticketsScheduleMap,
  Document,
  DocumentAcceptance,
  DocumentRead,
  DocumentVersion,
  EmergencyContact,
  Employee,
  EmployeeAbsenceSummary,
  EmployeeAttribute,
  EmployeeClockingProfile,
  EmployeeCostsByJobRoleAndLocation,
  EmployeeGroup,
  EmployeeInvite,
  EmployeeLeaveBalancesSummary,
  EmployeeLeaveSummary,
  EmployeeMessage,
  EmployeeMessageRecipient,
  EmployeeNotificationChannel,
  EmployeeOnboarding,
  EmployeePayDetails,
  EmployeePayPeriodSummary,
  EmployeePersonalDetails,
  FeaturesEnabled,
  History,
  IcalFeed,
  ICRTouchConnection,
  ICRTouchConnectionScheduleSitesConfig,
  JobRole,
  LeaveAdjustment,
  LeaveAllowanceChunk,
  LeavePeriod,
  LeavePolicy,
  LeaveRequest,
  LeaveSettings,
  LeaveType,
  ListEmployeeCostsByJobRoleAndLocationRequest,
  ListEmployeeLeaveBalanceSummariesRequest,
  ListEmployeePayPeriodSummariesRequest,
  ListEventEntitiesResponseDataEnum,
  ListTimesheetSummariesRequest,
  Location,
  NotificationSettings,
  OpenShiftResponse,
  PayCycle,
  PayPeriod,
  PayPeriodsSummary,
  PayType,
  PublicHoliday,
  Schedule,
  ScheduleEvent,
  ScheduleOverviewSummary,
  ScheduleTemplate,
  Shift,
  ShiftArea,
  ShiftAreaSession,
  ShiftConflict,
  ShiftSwap,
  ShiftTemplate,
  ShiftTemplateShiftAreaSession,
  ShowBudgetSummaryRequest,
  ShowDaySummaryRequest,
  ShowLeavePolicyRequest,
  ShowScheduleOverviewRequest,
  Tag,
  TimesheetEntry,
  TimesheetEntrySummary,
  TimesheetSettings,
  TrekksoftClientCredential,
  Unavailability,
  Upload,
  User,
  WorkPattern,
  WorkPatternOverride,
} from '../../../../api/v1';

// For cases when an entity is not defined in ListEventEntitiesResponseDataEnum, but we want to use it in the app.
export enum CustomEntity {
  EmployeePayPeriodSummary = 'EmployeePayPeriodSummary',
  EmployeeCostsByJobRoleAndLocation = 'EmployeeCostsByJobRoleAndLocation',
  EmployeeLeaveBalancesSummary = 'EmployeeLeaveBalancesSummary',
  FeaturesEnabled = 'FeaturesEnabled',
  PayPeriodsSummary = 'PayPeriodsSummary',
  CompanyOnboarding = 'CompanyOnboarding',
  EmployeeOnboarding = 'EmployeeOnboarding',
}

/**
 * To allow Typescript to understand the return type of our store functions, we need to map string representations
 * of our entities to actual Typescript interfaces.
 */
export type EntityInterfaceMap = {
  [CustomEntity.EmployeePayPeriodSummary]: EmployeePayPeriodSummary;
  [CustomEntity.EmployeeCostsByJobRoleAndLocation]: EmployeeCostsByJobRoleAndLocation;
  [CustomEntity.EmployeeLeaveBalancesSummary]: EmployeeLeaveBalancesSummary;
  [CustomEntity.FeaturesEnabled]: FeaturesEnabled;
  [CustomEntity.PayPeriodsSummary]: PayPeriodsSummary;
  [CustomEntity.CompanyOnboarding]: CompanyOnboarding;
  [CustomEntity.EmployeeOnboarding]: EmployeeOnboarding;

  [Entity.Absence]: Absence;
  [Entity.AbsenceCategory]: AbsenceCategory;
  [Entity.AccessRole]: AccessRole;
  [Entity.BillingCouponRedemption]: BillingCouponRedemption;
  [Entity.BillingDetail]: BillingDetails;
  [Entity.BillingPackage]: BillingPackage;
  [Entity.BillingPaymentMethod]: BillingPaymentMethod;
  [Entity.BillingProductPrice]: BillingProductPrice;
  [Entity.BillingWallet]: BillingWallet;
  [Entity.BillingWalletType]: BillingWalletType;
  [Entity.BudgetSummary]: BudgetSummary;
  [Entity.ClockInPortal]: ClockInPortal;
  [Entity.Company]: Company;
  [Entity.CompanyNotificationChannel]: CompanyNotificationChannel;
  [Entity.CompanySetting]: CompanySetting;
  [Entity.DaySummary]: DaySummary;
  [Entity.DigiticketsConnection]: DigiticketsConnection;
  [Entity.DigiticketsScheduleMap]: DigiticketsScheduleMap;
  [Entity.Document]: Document;
  [Entity.DocumentAcceptance]: DocumentAcceptance;
  [Entity.DocumentRead]: DocumentRead;
  [Entity.DocumentVersion]: DocumentVersion;
  [Entity.EmergencyContact]: EmergencyContact;
  [Entity.Employee]: Employee;
  [Entity.EmployeeAbsenceSummary]: EmployeeAbsenceSummary;
  [Entity.EmployeeAttribute]: EmployeeAttribute;
  [Entity.EmployeeClockingProfile]: EmployeeClockingProfile;
  [Entity.EmployeeGroup]: EmployeeGroup;
  [Entity.EmployeeInvite]: EmployeeInvite;
  [Entity.EmployeeLeaveSummary]: EmployeeLeaveSummary;
  [Entity.EmployeeMessage]: EmployeeMessage;
  [Entity.EmployeeMessageRecipient]: EmployeeMessageRecipient;
  [Entity.EmployeeNotificationChannel]: EmployeeNotificationChannel;
  [Entity.EmployeePayDetail]: EmployeePayDetails;
  [Entity.EmployeePersonalDetail]: EmployeePersonalDetails;
  [Entity.History]: History;
  [Entity.IcalFeed]: IcalFeed;
  [Entity.IcrTouchConnection]: ICRTouchConnection;
  [Entity.IcrTouchScheduleConfig]: ICRTouchConnectionScheduleSitesConfig;
  [Entity.JobRole]: JobRole;
  [Entity.LeaveAdjustment]: LeaveAdjustment;
  [Entity.LeavePeriod]: LeavePeriod;
  [Entity.LeavePolicy]: LeavePolicy;
  [Entity.LeaveRequest]: LeaveRequest;
  [Entity.LeaveSetting]: LeaveSettings;
  [Entity.LeaveType]: LeaveType;
  [Entity.Location]: Location;
  [Entity.NotificationSetting]: NotificationSettings;
  [Entity.OpenShiftResponse]: OpenShiftResponse;
  [Entity.PayCycle]: PayCycle;
  [Entity.PayPeriod]: PayPeriod;
  [Entity.PayType]: PayType;
  [Entity.PublicHoliday]: PublicHoliday;
  [Entity.Schedule]: Schedule;
  [Entity.ScheduleEvent]: ScheduleEvent;
  [Entity.ScheduleOverviewSummary]: ScheduleOverviewSummary;
  [Entity.ScheduleTemplate]: ScheduleTemplate;
  [Entity.Shift]: Shift;
  [Entity.ShiftArea]: ShiftArea;
  [Entity.ShiftSwap]: ShiftSwap;
  [Entity.ShiftTemplate]: ShiftTemplate;
  [Entity.Tag]: Tag;
  [Entity.TimesheetEntry]: TimesheetEntry;
  [Entity.TimesheetEntrySummary]: TimesheetEntrySummary;
  [Entity.TimesheetSetting]: TimesheetSettings;
  [Entity.TrekksoftClientCredential]: TrekksoftClientCredential;
  [Entity.Unavailability]: Unavailability;
  [Entity.Upload]: Upload;
  [Entity.User]: User;
  [Entity.WorkPattern]: WorkPattern;
  [Entity.WorkPatternOverride]: WorkPatternOverride;
};

export type EntityRelationMap = {
  [Entity.JobRole]: JobRole;
  [Entity.Location]: Location;
  [Entity.Schedule]: Schedule;
  [Entity.ShiftConflict]: ShiftConflict;
  [Entity.ShiftAreaSession]: ShiftAreaSession;
  [Entity.ShiftTemplateAreaSession]: ShiftTemplateShiftAreaSession;
  [Entity.Tag]: Tag;
  [Entity.LeaveAllowanceChunk]: LeaveAllowanceChunk;
};

export type EntityFetcherRequestMap = {
  [CustomEntity.EmployeePayPeriodSummary]: ListEmployeePayPeriodSummariesRequest;
  [CustomEntity.EmployeeCostsByJobRoleAndLocation]: ListEmployeeCostsByJobRoleAndLocationRequest;
  [CustomEntity.EmployeeLeaveBalancesSummary]: ListEmployeeLeaveBalanceSummariesRequest;
  [Entity.BudgetSummary]: ShowBudgetSummaryRequest;
  [Entity.DaySummary]: ShowDaySummaryRequest;
  [Entity.ScheduleOverviewSummary]: ShowScheduleOverviewRequest;
  [Entity.TimesheetEntrySummary]: ListTimesheetSummariesRequest;
  [Entity.LeavePolicy]: ShowLeavePolicyRequest;
};

export type EntityName = string & keyof EntityInterfaceMap;
export type WSEntityName = EntityName & ListEventEntitiesResponseDataEnum;

export type OrderBy<T extends object> = (
  | (keyof T & string)
  | `-${keyof T & string}`
)[];

export type GetParameterType<T> = T extends keyof EntityFetcherRequestMap &
  EntityName
  ? EntityFetcherRequestMap[T]
  : never;

export type EntityInstance<T extends EntityName> = EntityInterfaceMap[T];

export type Fetcher<
  InterfaceMap extends Record<string, object> = EntityInterfaceMap,
  Key extends keyof InterfaceMap = keyof InterfaceMap,
  ParamsMap extends Record<string, object> = EntityFetcherRequestMap,
> = (
  params: Key extends keyof ParamsMap ? ParamsMap[Key] : never,
  where: Where<InterfaceMap[Key]>,
  orderBy: OrderBy<InterfaceMap[Key]>,
) => Promise<InterfaceMap[Key][]>;

export type BatchFetcher<
  InterfaceMap extends Record<string, object> = EntityInterfaceMap,
  WSSupportName extends keyof InterfaceMap = WSEntityName,
  Key extends keyof InterfaceMap & WSSupportName = InterfaceMap & WSSupportName,
> = (ids: (number | string)[]) => Promise<InterfaceMap[Key][]>;

export enum PopulateStrategy {
  Disable = 'Disable',
  Subset = 'Subset', // Populate from any less strict collection
  Complete = 'Complete', // Populate only from a complete collection
}

export type EntityConfigRecord<
  InterfaceMap extends Record<string, object>,
  RelationMap extends Record<string, object>,
  Key extends keyof InterfaceMap,
  WSSupportName extends string,
  ParamsMap extends Record<string, object> = {},
> = {
  fetcher: Fetcher<InterfaceMap, Key, ParamsMap>;
  batchFetcher?: Key extends WSSupportName
    ? BatchFetcher<InterfaceMap, WSSupportName, Key>
    : never;
  entityMaker: (data: unknown) => InterfaceMap[Key];
  relations?: Array<keyof RelationMap & string>;
  triggers?: WSSupportName[];
  orderBy?: OrderBy<InterfaceMap[Key]>;
  populateStrategy?: PopulateStrategy;
  useWebsocket?: boolean;
};

export type EntityConfig<
  InterfaceMap extends Record<string, object>,
  RelationMap extends Record<string, object>,
  WSSupportName extends string = string,
  ParamsMap extends Record<string, object> = {},
> = {
  [K in keyof InterfaceMap]: EntityConfigRecord<
    InterfaceMap,
    RelationMap,
    K,
    WSSupportName,
    ParamsMap
  >;
};

export type EntityRelationConfig<
  InterfaceMap extends Record<string, object>,
  RelationMap extends Record<string, object>,
> = {
  [R in keyof RelationMap]: {
    entityMaker: (data: unknown) => RelationMap[R];
  } & Partial<{
    [K in keyof InterfaceMap]: {
      relationshipKeys:
        | {
            parentToChild: keyof InterfaceMap[K];
          }
        | { parentToChildIds: keyof InterfaceMap[K] }
        | {
            childToParent: keyof RelationMap[R];
          };
      detachHandler?: (
        entity: RelationMap[R],
        parentEntity: InterfaceMap[K],
      ) => boolean;
    };
  }>;
};
